import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Pencil, Archive, Trash2, X, Save, Image as ImageIcon, 
  Video, Search, Upload, Play, Link, RefreshCw, ExternalLink, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente de Preview de Imagem (fora do GalleryPage)
const ImagePreviewComponent = ({ src, previewKey, onError }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Reset state quando src ou previewKey mudam
  const currentKey = `${src}-${previewKey}`;
  const [lastKey, setLastKey] = useState(currentKey);
  
  if (currentKey !== lastKey) {
    setLastKey(currentKey);
    setHasError(false);
    setIsLoading(true);
  }

  if (!src) {
    return (
      <div className="h-40 w-40 bg-[#111] rounded-lg border border-dashed border-white/10 flex items-center justify-center text-gray-500 text-sm">
        <div className="text-center">
          <ImageIcon className="mx-auto mb-2" size={24} />
          Cole uma URL acima
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="h-40 w-40 bg-[#1a1a1a] rounded-lg border border-red-500/30 flex flex-col items-center justify-center text-red-400 text-xs p-4">
        <AlertCircle className="mb-2" size={24} />
        <span className="text-center">Não foi possível carregar a imagem</span>
        <span className="text-gray-500 text-[10px] mt-1 text-center">Verifique se a URL está correta ou faça um novo upload</span>
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      {isLoading && (
        <div className="absolute inset-0 bg-[#111] rounded-lg flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img 
        key={`img-${previewKey}`}
        src={src} 
        alt="Preview" 
        className={`h-40 max-w-xs rounded-lg object-cover border border-white/10 bg-[#111] ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        style={{ minWidth: '160px' }}
        onError={() => {
          setHasError(true);
          setIsLoading(false);
          if (onError) onError();
        }}
        onLoad={() => {
          setIsLoading(false);
        }}
      />
      {!isLoading && !hasError && (
        <p className="text-gray-600 text-xs mt-2 max-w-xs truncate">{src}</p>
      )}
    </div>
  );
};

const GalleryPage = () => {
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreviewKey, setImagePreviewKey] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    image: '',
    video_url: '',
    category: 'Evento',
    event_name: '',
    description: '',
    source_url: '',
    youtube_video_id: '',
    published: true
  });

  const categories = ['Evento', 'Show', 'Bastidores', 'Produção', 'Festival', 'Carnaval', 'Corporativo', 'Estrutura', 'Programa'];

  const openNewModal = useCallback(() => {
    setEditingItem(null);
    setFormData({
      title: '',
      image: '',
      video_url: '',
      category: 'Evento',
      event_name: '',
      description: '',
      source_url: '',
      youtube_video_id: '',
      published: true
    });
    setImagePreviewKey(prev => prev + 1);
    setImageError(false);
    setShowModal(true);
  }, []);

  const fetchGallery = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await axios.get(`${API}/gallery/all`, {
        headers: getAuthHeaders()
      });
      setGallery(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar galeria:', error);
      setErrorMessage('Erro ao carregar galeria. Tente atualizar a página.');
      setGallery([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchGallery();
    if (searchParams.get('action') === 'new') {
      openNewModal();
      setSearchParams({});
    }
  }, [fetchGallery, searchParams, setSearchParams, openNewModal]);

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      image: item.image || '',
      video_url: item.video_url || '',
      category: item.category || 'Evento',
      event_name: item.event_name || '',
      description: item.description || '',
      source_url: item.source_url || '',
      youtube_video_id: item.youtube_video_id || '',
      published: item.published !== false
    });
    setImagePreviewKey(prev => prev + 1);
    setImageError(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMessage('');

    try {
      const headers = getAuthHeaders();
      
      if (editingItem) {
        await axios.put(`${API}/gallery/${editingItem.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/gallery`, formData, { headers });
      }
      
      // Fechar modal primeiro
      setShowModal(false);
      setEditingItem(null);
      
      // Aguardar um pouco e buscar dados atualizados
      setTimeout(async () => {
        try {
          const response = await axios.get(`${API}/gallery/all`, { headers });
          setGallery(response.data || []);
        } catch (err) {
          console.error('Erro ao atualizar lista:', err);
          // Tenta buscar novamente
          fetchGallery();
        }
      }, 500);
      
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      const msg = error.response?.data?.detail || 'Erro ao salvar item. Tente novamente.';
      setErrorMessage(msg);
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const label = item.title || 'este item';
    const confirmed = window.confirm(
      `Arquivar "${label}"?\n\nO item será removido da galeria pública. Esta ação pode ser desfeita cadastrando o item novamente.`
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/gallery/${item.id}`, {
        headers: getAuthHeaders()
      });
      // Atualizar lista removendo o item localmente
      setGallery(prev => prev.filter(i => i.id !== item.id));
    } catch (error) {
      console.error('Erro ao arquivar item:', error);
      alert('Erro ao arquivar item. Tente novamente.');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida (JPG, PNG, GIF, etc)');
      return;
    }

    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('A imagem é muito grande. O tamanho máximo é 10MB.');
      return;
    }

    setUploading(true);
    setImageError(false);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Construir URL completa - usar BACKEND_URL para uploads via API
      const newImageUrl = `${BACKEND_URL}${response.data.url}`;
      console.log('Upload completo. Nova URL:', newImageUrl);
      setFormData(prev => ({ ...prev, image: newImageUrl }));
      setImagePreviewKey(prev => prev + 1);
      setImageError(false);
      
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else {
        alert('Erro ao fazer upload da imagem. Tente novamente.');
      }
    } finally {
      setUploading(false);
      // Limpar o input para permitir upload do mesmo arquivo
      if (e.target) e.target.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('video/')) {
      alert('Por favor, selecione um vídeo válido (MP4, MOV, WebM, etc)');
      return;
    }

    // Validar tamanho (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert('O vídeo é muito grande. O tamanho máximo é 100MB.');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/video`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Construir URL completa
      const newVideoUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, video_url: newVideoUrl }));
      
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else {
        alert('Erro ao fazer upload do vídeo. Tente novamente.');
      }
    } finally {
      setUploading(false);
      // Limpar o input para permitir upload do mesmo arquivo
      if (e.target) e.target.value = '';
    }
  };

  const handleImageUrlChange = (e) => {
    const newUrl = e.target.value;
    setFormData(prev => ({ ...prev, image: newUrl }));
    setImageError(false);
    // Força atualização do preview após um pequeno delay
    setTimeout(() => {
      setImagePreviewKey(prev => prev + 1);
    }, 100);
  };

  const refreshImagePreview = () => {
    setImageError(false);
    setImagePreviewKey(prev => prev + 1);
  };

  const clearImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
    setImageError(false);
    setImagePreviewKey(prev => prev + 1);
  };

  const clearVideo = () => {
    setFormData(prev => ({ ...prev, video_url: '' }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setImageError(false);
    setErrorMessage('');
  };

  const filteredGallery = gallery.filter(item => 
    (item.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.event_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Galeria</h1>
          <p className="text-gray-400">Gerencie fotos e vídeos dos momentos</p>
        </div>
        <button
          onClick={openNewModal}
          className="bg-gradient-to-r from-[#00E5FF] to-[#0A84FF] text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          Novo Item
        </button>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-400">{errorMessage}</span>
          <button 
            onClick={() => setErrorMessage('')}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
        <input
          type="text"
          placeholder="Buscar na galeria..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#00E5FF]"
        />
      </div>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredGallery.length === 0 ? (
        <div className="text-center py-16">
          <ImageIcon className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">Nenhum item encontrado</p>
          <button
            onClick={openNewModal}
            className="mt-4 text-[#00E5FF] hover:underline"
          >
            Adicionar primeiro item
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredGallery.map((item) => (
            <div
              key={item.id}
              className="bg-[#0a0a0a] border border-white/5 rounded-xl overflow-hidden group hover:border-white/10 transition-all relative"
            >
              <div className="relative aspect-square">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';
                  }}
                />
                {item.video_url && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                      <Play size={20} className="text-white ml-1" />
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute top-2 left-2 flex gap-2">
                  <span className="px-2 py-1 bg-[#00E5FF] text-black text-xs font-bold rounded">
                    {item.category}
                  </span>
                  {!item.published && (
                    <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">
                      Oculto
                    </span>
                  )}
                </div>
                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEditModal(item)}
                    className="p-2 bg-white/20 backdrop-blur rounded-lg hover:bg-white/30 transition-colors"
                    title="Editar"
                    data-testid={`admin-edit-gallery-${item.id}`}
                  >
                    <Pencil size={14} className="text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="flex items-center gap-1.5 px-2.5 py-2 bg-amber-600/80 backdrop-blur rounded-lg hover:bg-amber-600 transition-colors"
                    title="Arquivar item (remove da galeria pública)"
                    data-testid={`admin-archive-gallery-${item.id}`}
                  >
                    <Archive size={14} className="text-white" />
                    <span className="text-white text-xs font-semibold">Arquivar</span>
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <h3 className="text-white font-bold text-sm truncate">{item.title}</h3>
                  {item.event_name && (
                    <p className="text-gray-300 text-xs truncate">{item.event_name}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="w-full max-w-2xl mx-4 bg-[#0a0a0a] rounded-2xl border border-white/10">
            {/* Modal Header */}
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingItem ? 'Editar Item' : 'Novo Item da Galeria'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Título *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF]"
                  placeholder="Título do item"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="flex items-center gap-2">
                    <Link size={14} />
                    URL da Imagem * (Cole o link do Instagram, Google Drive, etc)
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={formData.image}
                    onChange={handleImageUrlChange}
                    className="flex-1 bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF]"
                    placeholder="https://exemplo.com/imagem.jpg ou cole link do Instagram"
                  />
                  <button
                    type="button"
                    onClick={refreshImagePreview}
                    className="px-3 py-3 bg-[#111] border border-white/10 rounded-lg text-[#00E5FF] hover:border-[#00E5FF] transition-colors"
                    title="Atualizar preview"
                  >
                    <RefreshCw size={20} />
                  </button>
                  {formData.image && (
                    <button
                      type="button"
                      onClick={clearImage}
                      className="px-3 py-3 bg-[#111] border border-white/10 rounded-lg text-red-500 hover:border-red-500 hover:bg-red-500/10 transition-colors"
                      title="Limpar imagem"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
                
                {/* Opção de Upload */}
                <div className="mt-3 flex items-center gap-4">
                  <span className="text-gray-500 text-sm">ou</span>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className={`flex items-center gap-2 px-4 py-2 bg-[#111] border border-dashed rounded-lg transition-colors ${
                      uploading 
                        ? 'border-[#00E5FF] text-[#00E5FF] cursor-wait' 
                        : 'border-white/20 text-gray-400 hover:border-[#00E5FF] hover:text-[#00E5FF]'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
                        <span>Enviando imagem...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Fazer upload do computador</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Image Preview */}
                <div className="mt-4">
                  <p className="text-gray-500 text-xs mb-2">Preview da imagem:</p>
                  <ImagePreviewComponent 
                    src={formData.image} 
                    previewKey={imagePreviewKey}
                    onError={() => setImageError(true)}
                  />
                </div>
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="flex items-center gap-2">
                    <Video size={14} />
                    URL do Vídeo (YouTube, Instagram, etc) - opcional
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="flex-1 bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF]"
                    placeholder="https://youtube.com/watch?v=... ou link do Instagram"
                  />
                  {formData.video_url && (
                    <button
                      type="button"
                      onClick={clearVideo}
                      className="px-3 py-3 bg-[#111] border border-white/10 rounded-lg text-red-500 hover:border-red-500 hover:bg-red-500/10 transition-colors"
                      title="Limpar vídeo"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
                
                {/* Opção de Upload de Vídeo */}
                <div className="mt-3 flex items-center gap-4">
                  <span className="text-gray-500 text-sm">ou</span>
                  <input
                    type="file"
                    ref={videoInputRef}
                    onChange={handleVideoUpload}
                    accept="video/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploading}
                    className={`flex items-center gap-2 px-4 py-2 bg-[#111] border border-dashed rounded-lg transition-colors ${
                      uploading 
                        ? 'border-[#0A84FF] text-[#0A84FF] cursor-wait' 
                        : 'border-white/20 text-gray-400 hover:border-[#0A84FF] hover:text-[#0A84FF]'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#0A84FF] border-t-transparent rounded-full animate-spin" />
                        <span>Enviando vídeo...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Fazer upload de vídeo</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Video Preview */}
                {formData.video_url && (
                  <div className="mt-4">
                    <p className="text-gray-500 text-xs mb-2">Preview do vídeo:</p>
                    {formData.video_url.includes('youtube.com') || formData.video_url.includes('youtu.be') ? (
                      <div className="relative rounded-lg overflow-hidden border border-white/10" style={{ maxWidth: '400px' }}>
                        <iframe
                          width="100%"
                          height="225"
                          src={`https://www.youtube.com/embed/${
                            formData.video_url.includes('youtu.be') 
                              ? formData.video_url.split('youtu.be/')[1]?.split('?')[0]
                              : formData.video_url.split('v=')[1]?.split('&')[0]
                          }`}
                          title="Preview do vídeo"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="bg-black"
                        />
                      </div>
                    ) : formData.video_url.includes('/uploads/') || formData.video_url.endsWith('.mp4') || formData.video_url.endsWith('.webm') || formData.video_url.endsWith('.mov') ? (
                      <div className="relative rounded-lg overflow-hidden border border-white/10" style={{ maxWidth: '400px' }}>
                        <video 
                          controls 
                          className="w-full bg-black"
                          style={{ maxHeight: '225px' }}
                        >
                          <source src={formData.video_url} type="video/mp4" />
                          Seu navegador não suporta vídeo.
                        </video>
                      </div>
                    ) : (
                      <div className="p-4 bg-[#111] rounded-lg border border-white/10" style={{ maxWidth: '400px' }}>
                        <div className="flex items-center gap-3">
                          <Play size={24} className="text-[#0A84FF]" />
                          <div>
                            <p className="text-white text-sm font-medium">Link de vídeo detectado</p>
                            <p className="text-gray-500 text-xs truncate" style={{ maxWidth: '300px' }}>{formData.video_url}</p>
                            <a 
                              href={formData.video_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#00E5FF] text-xs hover:underline mt-1 inline-block"
                            >
                              Abrir em nova aba para testar →
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Category and Event Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Categoria *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF]"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nome do Evento</label>
                  <input
                    type="text"
                    value={formData.event_name}
                    onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                    className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF]"
                    placeholder="Ex: Festival de Verão"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Descrição</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF] resize-none"
                  placeholder="Descrição do momento..."
                />
              </div>

              {/* Source URL - Link Original */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="flex items-center gap-2">
                    <ExternalLink size={14} />
                    Link da Fonte Original (Instagram, Facebook, etc)
                  </span>
                </label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00E5FF]"
                  placeholder="https://instagram.com/p/... ou link da matéria original"
                />
                <p className="text-gray-600 text-xs mt-2">
                  Cole aqui o link original de onde veio a imagem/vídeo (Instagram, Facebook, site, etc)
                </p>
              </div>

              {/* Published */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, published: !formData.published })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.published ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    formData.published ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-gray-400">
                  {formData.published ? 'Visível no site' : 'Oculto'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:border-white/30 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="flex-1 py-3 bg-gradient-to-r from-[#00E5FF] to-[#0A84FF] rounded-lg text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      {editingItem ? 'Salvar Alterações' : 'Adicionar'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
